import { ArrowRight, Utensils, Scale, Home, FileText, Store, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const largeTemplates = [
  {
    icon: Utensils,
    name: "Cloud Kitchen OS",
    stats: "7 tables · 3 automations",
    proof: "Runs 400+ orders daily at DabbaDaily",
    gradient: "from-orange-500 to-red-500",
  },
  {
    icon: Scale,
    name: "Legal Case Tracker",
    stats: "6 tables · 2 automations",
    proof: "Managing ₹140Cr in active cases",
    gradient: "from-purple-500 to-violet-500",
  },
];

const smallTemplates = [
  { icon: Home, name: "Real Estate CRM", gradient: "from-blue-500 to-indigo-500" },
  { icon: FileText, name: "TenderPilot", gradient: "from-emerald-500 to-teal-500" },
  { icon: Store, name: "Franchise Ops", gradient: "from-pink-500 to-rose-500" },
  { icon: Calendar, name: "Event Manager", gradient: "from-cyan-500 to-blue-500" },
];

export const TemplatesProof = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 sm:py-20 md:py-28 border-t border-border bg-muted/20">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="mb-8 sm:mb-12">
          <span className="text-xs font-mono text-muted-foreground block mb-3 sm:mb-4">
            [PRODUCTION]
          </span>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">
            Built for how you actually work
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            Production systems—not demos. Each template runs real businesses.
          </p>
        </div>
        
        {/* Asymmetric grid */}
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-4 sm:gap-6 mb-8 sm:mb-10">
          {/* Large cards - left */}
          <div className="space-y-4 sm:space-y-6">
            {largeTemplates.map((template, idx) => (
              <div
                key={idx}
                className="p-4 sm:p-6 rounded-xl border border-border bg-card hover-lift cursor-pointer group touch-target"
                onClick={() => navigate("/templates")}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br ${template.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <template.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-1">{template.name}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">{template.stats}</p>
                    <p className="text-xs sm:text-sm font-medium text-accent truncate">{template.proof}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Small cards - right stack */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {smallTemplates.map((template, idx) => (
              <div
                key={idx}
                className="p-3 sm:p-4 rounded-xl border border-border bg-card hover-lift cursor-pointer group touch-target"
                onClick={() => navigate("/templates")}
              >
                <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gradient-to-br ${template.gradient} flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-300`}>
                  <template.icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <h3 className="text-xs sm:text-sm font-semibold">{template.name}</h3>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer link */}
        <div className="text-center">
          <Button 
            variant="ghost" 
            className="text-muted-foreground hover:text-foreground group touch-target"
            onClick={() => navigate("/templates")}
          >
            Browse all templates
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </div>
    </section>
  );
};
